import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Users, Calendar, FileText, Activity, Filter, Download, Edit, Trash2, UserCog, UserPlus } from 'lucide-react';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [applications, setApplications] = useState([]);
  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [deleteType, setDeleteType] = useState(''); // 'application' or 'user'
  const [interviewDate, setInterviewDate] = useState('');
  const [interviewTime, setInterviewTime] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [newUserPhone, setNewUserPhone] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserUsername, setNewUserUsername] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('applications');
  
  // Check if current user is developer
  const isDeveloper = user?.role === 'developer';

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/admin-login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    if (parsedUser.role === 'parent') {
      navigate('/parent-dashboard');
      return;
    }

    setUser(parsedUser);
    fetchData(token, parsedUser.role);
  }, [navigate]);

  const fetchData = async (token, userRole) => {
    try {
      const promises = [
        axios.get(`${API}/applications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/audit-logs`, { headers: { Authorization: `Bearer ${token}` } })
      ];
      
      // Only fetch registered users for developer role
      if (userRole === 'developer') {
        promises.push(axios.get(`${API}/users/registered`, { headers: { Authorization: `Bearer ${token}` } }));
      }
      
      const results = await Promise.all(promises);
      
      setApplications(results[0].data);
      setAuditLogs(results[1].data);
      if (userRole === 'developer' && results[2]) {
        setRegisteredUsers(results[2].data);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/admin-login');
  };

  const handleScheduleInterview = async () => {
    if (!interviewDate || !interviewTime) {
      toast.error('Please select date and time');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/interviews/schedule`,
        {
          application_id: selectedApplication.id,
          interview_date: interviewDate,
          interview_time: interviewTime
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Interview scheduled successfully!');
        setShowScheduleModal(false);
        fetchData(token, user?.role);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule interview');
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) {
      toast.error('Please select a status');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/applications/status`,
        {
          application_id: selectedApplication.id,
          status: newStatus
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Status updated successfully!');
        setShowStatusModal(false);
        fetchData(token, user?.role);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleEditUser = async () => {
    if (!editPhone && !editEmail) {
      toast.error('Please enter phone or email to update');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/users/update`,
        {
          user_id: selectedUser.id,
          phone: editPhone || undefined,
          email: editEmail || undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('User details updated successfully!');
        setShowEditUserModal(false);
        setEditPhone('');
        setEditEmail('');
        fetchData(token, user?.role);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleAddUser = async () => {
    if (!newUserPhone || !newUserEmail) {
      toast.error('Please enter phone and email');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/users/create`,
        {
          phone: newUserPhone,
          email: newUserEmail,
          username: newUserUsername || undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`User created! Username: ${response.data.username}, Password: ${response.data.password}`);
        setShowAddUserModal(false);
        setNewUserPhone('');
        setNewUserEmail('');
        setNewUserUsername('');
        fetchData(token, user?.role);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteApplication = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API}/applications/${selectedApplication.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Application deleted successfully!');
        setShowDeleteConfirmModal(false);
        setSelectedApplication(null);
        fetchData(token, user?.role);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete application');
    }
  };

  const handleDeleteUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API}/users/${selectedUser.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('User deleted successfully!');
        setShowDeleteConfirmModal(false);
        setSelectedUser(null);
        fetchData(token, user?.role);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const filteredApplications = filterStatus === 'all'
    ? applications
    : applications.filter(app => app.status === filterStatus);

  const handleExportExcel = () => {
    if (applications.length === 0) {
      toast.error('No applications to export');
      return;
    }

    // Prepare data for Excel
    const excelData = applications.map(app => ({
      'Admission No': app.admission_no,
      'Pupil Name': app.pupil_name,
      'Date of Birth': app.dob,
      'Class Sought': app.class_sought,
      'Father Name': app.father_name,
      'Father Contact': app.father_contact,
      'Father Email': app.father_email,
      'Mother Name': app.mother_name,
      'Mother Contact': app.mother_contact,
      'Mother Email': app.mother_email,
      'Address': app.address,
      'Status': app.status,
      'Interview Date': app.interview_date || 'Not Scheduled',
      'Interview Time': app.interview_time || '-',
      'Application Date': new Date(app.created_at).toLocaleDateString()
    }));

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    
    // Set column widths
    const colWidths = [
      { wch: 15 }, // Admission No
      { wch: 25 }, // Pupil Name
      { wch: 12 }, // DOB
      { wch: 12 }, // Class
      { wch: 25 }, // Father Name
      { wch: 15 }, // Father Contact
      { wch: 30 }, // Father Email
      { wch: 25 }, // Mother Name
      { wch: 15 }, // Mother Contact
      { wch: 30 }, // Mother Email
      { wch: 40 }, // Address
      { wch: 20 }, // Status
      { wch: 15 }, // Interview Date
      { wch: 15 }, // Interview Time
      { wch: 15 }  // Application Date
    ];
    worksheet['!cols'] = colWidths;

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Applications');

    // Generate filename with date
    const filename = `School_Admissions_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Download
    XLSX.writeFile(workbook, filename);
    toast.success('Excel file downloaded successfully!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F9FAFB' }}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b" style={{ borderColor: '#E5E7EB' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }} data-testid="admin-dashboard-heading">Admin Dashboard</h1>
            <p className="text-sm text-gray-600">Role: {user?.role} | Welcome, {user?.username}</p>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition" data-testid="admin-logout-btn">
            <LogOut size={20} />
            Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className={`grid gap-6 mb-8 ${isDeveloper ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
          {isDeveloper && (
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Registered Users</p>
                  <p className="text-3xl font-bold" style={{ color: '#002147' }} data-testid="total-users">{registeredUsers.length}</p>
                </div>
                <UserCog size={40} color="#002147" />
              </div>
            </div>
          )}

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Applications</p>
                <p className="text-3xl font-bold" style={{ color: '#002147' }} data-testid="total-applications">{applications.length}</p>
              </div>
              <Users size={40} color="#002147" />
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Received</p>
                <p className="text-3xl font-bold" style={{ color: '#3B82F6' }}>{applications.filter(a => a.status === 'Application Received').length}</p>
              </div>
              <FileText size={40} color="#3B82F6" />
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Scheduled</p>
                <p className="text-3xl font-bold" style={{ color: '#7C3AED' }}>{applications.filter(a => a.status === 'Interview Scheduled').length}</p>
              </div>
              <Calendar size={40} color="#7C3AED" />
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Selected</p>
                <p className="text-3xl font-bold" style={{ color: '#047857' }}>{applications.filter(a => a.status === 'Selected').length}</p>
              </div>
              <Activity size={40} color="#047857" />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 flex-wrap">
          <button
            onClick={() => setActiveTab('applications')}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              activeTab === 'applications' 
                ? 'bg-[#002147] text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="applications-tab"
          >
            Applications
          </button>
          {isDeveloper && (
            <button
              onClick={() => setActiveTab('users')}
              className={`px-6 py-3 rounded-lg font-semibold transition flex items-center gap-2 ${
                activeTab === 'users' 
                  ? 'bg-[#002147] text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              data-testid="users-tab"
            >
              <UserCog size={20} />
              Registered Users
            </button>
          )}
          <button
            onClick={() => setActiveTab('audit')}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              activeTab === 'audit' 
                ? 'bg-[#002147] text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="audit-tab"
          >
            Audit Logs
          </button>
        </div>

        {/* Applications Tab */}
        {activeTab === 'applications' && (
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }}>Applications</h2>
              
              <div className="flex items-center gap-4">
                <button
                  onClick={handleExportExcel}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                  data-testid="export-excel-btn"
                >
                  <Download size={20} />
                  Export Excel
                </button>
                
                <div className="flex items-center gap-2">
                  <Filter size={20} color="#6B7280" />
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg"
                    data-testid="status-filter"
                  >
                    <option value="all">All Status</option>
                    <option value="Application Received">Application Received</option>
                    <option value="Interview Scheduled">Interview Scheduled</option>
                    <option value="Selected">Selected</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b" style={{ borderColor: '#E5E7EB' }}>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Admission No</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Pupil Name</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Father Name</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Class</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Interview Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApplications.map(app => (
                    <tr key={app.id} className="border-b hover:bg-gray-50" style={{ borderColor: '#E5E7EB' }} data-testid={`application-row-${app.admission_no}`}>
                      <td className="py-3 px-4">{app.admission_no}</td>
                      <td className="py-3 px-4">{app.pupil_name}</td>
                      <td className="py-3 px-4">{app.father_name}</td>
                      <td className="py-3 px-4">{app.class_sought}</td>
                      <td className="py-3 px-4">
                        {app.interview_date ? (
                          <div>
                            <p className="font-medium text-purple-700">{app.interview_date}</p>
                            <p className="text-xs text-purple-600">{app.interview_time}</p>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">Not Scheduled</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`status-badge status-${app.status.toLowerCase().replace(' ', '-')}`}>
                          {app.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => navigate(`/admin/application/${app.id}`)}
                            className="px-3 py-1 bg-green-100 text-green-700 rounded text-sm hover:bg-green-200 transition"
                            data-testid={`view-btn-${app.admission_no}`}
                          >
                            View
                          </button>
                          <button
                            onClick={() => {
                              setSelectedApplication(app);
                              setShowScheduleModal(true);
                            }}
                            className="px-3 py-1 bg-purple-100 text-purple-700 rounded text-sm hover:bg-purple-200 transition"
                            data-testid={`schedule-btn-${app.admission_no}`}
                          >
                            Schedule
                          </button>
                          <button
                            onClick={() => {
                              setSelectedApplication(app);
                              setShowStatusModal(true);
                            }}
                            className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200 transition"
                            data-testid={`status-btn-${app.admission_no}`}
                          >
                            Status
                          </button>
                          {isDeveloper && (
                            <button
                              onClick={() => {
                                setSelectedApplication(app);
                                setDeleteType('application');
                                setShowDeleteConfirmModal(true);
                              }}
                              className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200 transition flex items-center gap-1"
                              data-testid={`delete-btn-${app.admission_no}`}
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredApplications.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No applications found
                </div>
              )}
            </div>
          </div>
        )}

        {/* Registered Users Tab - Developer Only */}
        {activeTab === 'users' && isDeveloper && (
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }}>Registered Users</h2>
              <div className="flex items-center gap-4">
                <p className="text-sm text-gray-600">Total: {registeredUsers.length} users</p>
                <button
                  onClick={() => setShowAddUserModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                  data-testid="add-user-btn"
                >
                  <UserPlus size={20} />
                  Add New User
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b" style={{ borderColor: '#E5E7EB' }}>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Username</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Phone</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Admission No</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Application Status</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Registered On</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {registeredUsers.map(regUser => (
                    <tr key={regUser.id} className="border-b hover:bg-gray-50" style={{ borderColor: '#E5E7EB' }} data-testid={`user-row-${regUser.username}`}>
                      <td className="py-3 px-4 font-medium">{regUser.username}</td>
                      <td className="py-3 px-4">{regUser.phone}</td>
                      <td className="py-3 px-4">{regUser.email}</td>
                      <td className="py-3 px-4">
                        {regUser.admission_no ? (
                          <span className="font-medium text-blue-700">{regUser.admission_no}</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          regUser.application_status === 'Not Submitted' 
                            ? 'bg-gray-100 text-gray-600' 
                            : regUser.application_status === 'Selected'
                            ? 'bg-green-100 text-green-700'
                            : regUser.application_status === 'Rejected'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {regUser.application_status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {regUser.created_at ? new Date(regUser.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setSelectedUser(regUser);
                              setEditPhone(regUser.phone || '');
                              setEditEmail(regUser.email || '');
                              setShowEditUserModal(true);
                            }}
                            className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200 transition flex items-center gap-1"
                            data-testid={`edit-user-btn-${regUser.username}`}
                          >
                            <Edit size={14} />
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(regUser);
                              setDeleteType('user');
                              setShowDeleteConfirmModal(true);
                            }}
                            className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200 transition flex items-center gap-1"
                            data-testid={`delete-user-btn-${regUser.username}`}
                          >
                            <Trash2 size={14} />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {registeredUsers.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No registered users found
                </div>
              )}
            </div>
          </div>
        )}

        {/* Audit Logs Tab */}
        {activeTab === 'audit' && (
          <div className="card">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }}>Audit Logs</h2>
            
            <div className="space-y-4">
              {auditLogs.map(log => (
                <div key={log.id} className="p-4 border rounded-lg" style={{ borderColor: '#E5E7EB' }} data-testid={`audit-log-${log.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                        {log.admin_role}
                      </span>
                      <span className="font-semibold" style={{ color: '#002147' }}>{log.action}</span>
                    </div>
                    <span className="text-sm text-gray-600">{new Date(log.timestamp).toLocaleString()}</span>
                  </div>
                  <p className="text-gray-600 text-sm">{log.details}</p>
                </div>
              ))}

              {auditLogs.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No audit logs found
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Schedule Interview Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="schedule-modal">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }}>Schedule Interview</h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Application: {selectedApplication?.admission_no}</p>
              <p className="font-semibold">{selectedApplication?.pupil_name}</p>
            </div>

            <div className="mb-4">
              <label className="form-label">Interview Date</label>
              <input
                type="date"
                value={interviewDate}
                onChange={(e) => setInterviewDate(e.target.value)}
                className="form-input"
                data-testid="interview-date-input"
              />
            </div>

            <div className="mb-6">
              <label className="form-label">Interview Time (10 AM - 2 PM)</label>
              <select
                value={interviewTime}
                onChange={(e) => setInterviewTime(e.target.value)}
                className="form-input"
                data-testid="interview-time-select"
              >
                <option value="">Select time</option>
                <option value="10:00 AM">10:00 AM</option>
                <option value="10:15 AM">10:15 AM</option>
                <option value="10:30 AM">10:30 AM</option>
                <option value="10:45 AM">10:45 AM</option>
                <option value="11:00 AM">11:00 AM</option>
                <option value="11:15 AM">11:15 AM</option>
                <option value="11:30 AM">11:30 AM</option>
                <option value="11:45 AM">11:45 AM</option>
                <option value="12:00 PM">12:00 PM</option>
                <option value="12:15 PM">12:15 PM</option>
                <option value="12:30 PM">12:30 PM</option>
                <option value="12:45 PM">12:45 PM</option>
                <option value="01:00 PM">01:00 PM</option>
                <option value="01:15 PM">01:15 PM</option>
                <option value="01:30 PM">01:30 PM</option>
                <option value="01:45 PM">01:45 PM</option>
              </select>
            </div>

            <div className="flex gap-4">
              <button onClick={handleScheduleInterview} className="primary-btn flex-1" data-testid="confirm-schedule-btn">
                Schedule
              </button>
              <button onClick={() => setShowScheduleModal(false)} className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50" data-testid="cancel-schedule-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Status Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="status-modal">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }}>Update Status</h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Application: {selectedApplication?.admission_no}</p>
              <p className="font-semibold">{selectedApplication?.pupil_name}</p>
              <p className="text-sm text-gray-600 mt-2">Current Status: <span className={`status-badge status-${selectedApplication?.status.toLowerCase().replace(' ', '-')}`}>{selectedApplication?.status}</span></p>
            </div>

            <div className="mb-6">
              <label className="form-label">New Status</label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="form-input"
                data-testid="status-select"
              >
                <option value="">Select status</option>
                <option value="Application Received">Application Received</option>
                <option value="Interview Scheduled">Interview Scheduled</option>
                <option value="Selected">Selected</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>

            <div className="flex gap-4">
              <button onClick={handleUpdateStatus} className="primary-btn flex-1" data-testid="confirm-status-btn">
                Update
              </button>
              <button onClick={() => setShowStatusModal(false)} className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50" data-testid="cancel-status-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="edit-user-modal">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }}>Edit User Details</h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Username: <span className="font-semibold">{selectedUser?.username}</span></p>
            </div>

            <div className="mb-4">
              <label className="form-label">Phone Number</label>
              <input
                type="tel"
                value={editPhone}
                onChange={(e) => setEditPhone(e.target.value)}
                className="form-input"
                placeholder="Enter phone number"
                data-testid="edit-phone-input"
              />
            </div>

            <div className="mb-6">
              <label className="form-label">Email Address</label>
              <input
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                className="form-input"
                placeholder="Enter email address"
                data-testid="edit-email-input"
              />
            </div>

            <div className="flex gap-4">
              <button onClick={handleEditUser} className="primary-btn flex-1" data-testid="confirm-edit-btn">
                Save Changes
              </button>
              <button 
                onClick={() => {
                  setShowEditUserModal(false);
                  setEditPhone('');
                  setEditEmail('');
                }} 
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50" 
                data-testid="cancel-edit-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="delete-confirm-modal">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <h3 className="text-2xl font-bold mb-4 text-red-600">Confirm Delete</h3>
            
            {deleteType === 'application' ? (
              <div className="mb-6">
                <p className="text-gray-700 mb-2">Are you sure you want to delete this application?</p>
                <p className="text-sm text-gray-600">
                  <strong>Admission No:</strong> {selectedApplication?.admission_no}<br />
                  <strong>Pupil Name:</strong> {selectedApplication?.pupil_name}
                </p>
                <p className="text-sm text-red-600 mt-4">
                  ⚠️ This action cannot be undone. All associated documents will also be deleted.
                </p>
              </div>
            ) : (
              <div className="mb-6">
                <p className="text-gray-700 mb-2">Are you sure you want to delete this user?</p>
                <p className="text-sm text-gray-600">
                  <strong>Username:</strong> {selectedUser?.username}<br />
                  <strong>Email:</strong> {selectedUser?.email}
                </p>
                <p className="text-sm text-red-600 mt-4">
                  ⚠️ This action cannot be undone. User's application (if any) will also be deleted.
                </p>
              </div>
            )}

            <div className="flex gap-4">
              <button 
                onClick={deleteType === 'application' ? handleDeleteApplication : handleDeleteUser} 
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold"
                data-testid="confirm-delete-btn"
              >
                Yes, Delete
              </button>
              <button 
                onClick={() => {
                  setShowDeleteConfirmModal(false);
                  setSelectedApplication(null);
                  setSelectedUser(null);
                }} 
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50" 
                data-testid="cancel-delete-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New User Modal - Developer Only */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="add-user-modal">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }}>Add New User</h3>
            
            <p className="text-sm text-gray-600 mb-4">
              Manually register a new user. They will receive login credentials via email.
            </p>

            <div className="mb-4">
              <label className="form-label">Phone Number *</label>
              <input
                type="tel"
                value={newUserPhone}
                onChange={(e) => setNewUserPhone(e.target.value)}
                className="form-input"
                placeholder="+91XXXXXXXXXX"
                data-testid="new-user-phone-input"
              />
            </div>

            <div className="mb-4">
              <label className="form-label">Email Address *</label>
              <input
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                className="form-input"
                placeholder="parent@email.com"
                data-testid="new-user-email-input"
              />
            </div>

            <div className="mb-6">
              <label className="form-label">Username (Optional)</label>
              <input
                type="text"
                value={newUserUsername}
                onChange={(e) => setNewUserUsername(e.target.value)}
                className="form-input"
                placeholder="Leave blank for auto-generated"
                data-testid="new-user-username-input"
              />
            </div>

            <div className="flex gap-4">
              <button onClick={handleAddUser} className="primary-btn flex-1" data-testid="confirm-add-user-btn">
                Create User
              </button>
              <button 
                onClick={() => {
                  setShowAddUserModal(false);
                  setNewUserPhone('');
                  setNewUserEmail('');
                  setNewUserUsername('');
                }} 
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50" 
                data-testid="cancel-add-user-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
